import type {ComponentProps, ReactNode} from 'react';
import {Toolbar, ToolbarButton} from '@mui/x-data-grid';
import {ColumnsPanelTrigger, FilterPanelTrigger} from '@mui/x-data-grid-premium';
import {Stack, Tooltip, Typography} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '~/components/icons/svg/ViewColumnIcon.svg?react';
import {DataGridHeaderFiltersToggleButton} from './DataGridHeaderFiltersToggleButton';

declare module '@mui/x-data-grid' {
    interface ToolbarPropsOverrides {
        headerFiltersVisible?: boolean;
        onToggleHeaderFilters?: () => void;
        showFilterPanel?: boolean;
    }
}

declare module '@mui/x-data-grid-premium' {
    interface ToolbarPropsOverrides {
        headerFiltersVisible?: boolean;
        onToggleHeaderFilters?: () => void;
        showFilterPanel?: boolean;
    }
}

export type DataGridToolbarProps = ComponentProps<typeof Toolbar> & {
    /** Optional heading shown on the left of the toolbar. */
    title?: ReactNode;
    /** Whether the header filter row is visible. */
    headerFiltersVisible?: boolean;
    /** Toggles the header filter row. Injected by `CustomDataGrid`. */
    onToggleHeaderFilters?: () => void;
    /** Whether the filter-panel button is shown. Defaults to true. */
    showFilterPanel?: boolean;
};

export const DataGridToolbar = ({
                                    children,
                                    title,
                                    headerFiltersVisible = false,
                                    onToggleHeaderFilters,
                                    showFilterPanel = true,
                                    ...props
                                }: DataGridToolbarProps) => (
    <Toolbar {...props}>
        {title && <Typography variant="body1" sx={{marginRight: 'auto'}}>{title}</Typography>}

        {/* minWidth 0 lets content that can shorten (an ellipsised breadcrumb) give way to the toolbar buttons. */}
        <Stack direction="row" sx={{flex: 1, gap: 0.5, alignItems: 'center', minWidth: 0}}>
            {children}
        </Stack>

        {onToggleHeaderFilters && (
            <DataGridHeaderFiltersToggleButton
                visible={headerFiltersVisible}
                onToggle={onToggleHeaderFilters}
            />
        )}
        {showFilterPanel && (
            <Tooltip title="Filters">
                <FilterPanelTrigger render={<ToolbarButton color="primary"/>}>
                    <FilterListIcon fontSize="small"/>
                </FilterPanelTrigger>
            </Tooltip>
        )}
        <Tooltip title="Columns">
            <ColumnsPanelTrigger render={<ToolbarButton color="primary"/>}>
                <ViewColumnIcon />
            </ColumnsPanelTrigger>
        </Tooltip>
    </Toolbar>
);
